import { useMemo, useState } from "react";

import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";

import useCreateSavedSearch from "@/Apollo/hooks/useCreateSavedSearch";
import { useSavedSearches } from "@/Apollo/hooks/useSavedSearches";
import NotificationBellIcon from "@/assets/icons/24/NotificationBell";
import { useAuth } from "@/auth";
import { useGuestModalGuard } from "@/common/hooks/guest.hooks";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { ACCOUNT_SEARCHES_ROUTE } from "@/common/static/routes.static";
import { SoldAlternativeFilter } from "@/common/utils/sold-alternatives.utils";
import addToast from "@/toast/addToast";
import { InfoPanel } from "@/ui/InfoPanel";
import { Button } from "@/ui/cva/Button";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";

/**
 * "Tell me when something like this is listed" on a sold PDP.
 *
 * A sold listing is the one moment we know exactly what a visitor wanted
 * and cannot sell it to them. A saved search turns that dead end into a
 * standing intent, and the backend for it already exists: this wires the
 * L0 facet set of the alternatives ladder into the same
 * `createSavedSearch` mutation the listing page's save button uses, so the
 * alert lands in `/account/searches` and fires through the existing
 * notification path with no new infrastructure.
 *
 * L0 rather than the rung that actually served the page: the alert is a
 * promise about a genuinely comparable piece, so it should be the
 * strictest description of the item, not the loosest one that happened to
 * return something today.
 *
 * Four states, all real, none of them a dead button:
 *  - signed out: the click opens the existing signup/login wall, and the
 *    save completes afterwards rather than being dropped, because
 *    `guestGuard` resolves true once the wall closes on a success.
 *  - already saved: the button is replaced by a confirmation that links to
 *    the account page where the alert can be turned off.
 *  - saving: disabled, so the mutation cannot be fired twice. (The listing
 *    page's version of this button can be double-clicked into creating two
 *    saved searches; this one cannot.)
 *  - failed: an error toast, and the button returns to its normal state so
 *    it can be retried.
 */

type SoldSimilarItemAlertProps = {
  /** L0 facet set, computed server-side by the ladder. */
  filters: SoldAlternativeFilter[];
  /** Human-readable name for the saved search row. */
  title: string;
  className?: string;
};

/**
 * Order-independent identity for a filter set, so an alert saved today
 * still matches one saved before the filter emission order changed.
 */
const filterKey = (filters: SoldAlternativeFilter[]) =>
  filters
    .map(filter => `${filter.key}=${filter.value}`)
    .sort()
    .join("&");

/** How many saved searches to scan for an existing match. */
const SAVED_SEARCH_SCAN_SIZE = 100;

/**
 * Only the fields the already-saved comparison reads.
 *
 * Deliberately structural rather than the generated `SavedSearchesQuery`
 * types, for the same reason `sold-alternatives.utils.ts` avoids them:
 * `src/Apollo/schema.ts` is gitignored codegen, so anything that leans on
 * its exact shape cannot be reasoned about or tested without a gateway
 * token. `key` and `value` are `unknown` because `key` generates as an
 * enum and `value` as a string, and both are stringified before use.
 */
type SavedSearchFilterLike = { key?: unknown; value?: unknown } | null;
type SavedSearchLike = { filters?: SavedSearchFilterLike[] | null } | null;

export const SoldSimilarItemAlert = ({
  filters,
  title,
  className,
}: SoldSimilarItemAlertProps) => {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const { guestGuard } = useGuestModalGuard();

  const [createSavedSearch, { loading }] = useCreateSavedSearch();
  const [justSaved, setJustSaved] = useState(false);

  // Only meaningful for a signed-in member, and saved searches are
  // per-member, so there is nothing to ask for while signed out.
  const { data } = useSavedSearches({
    variables: { pageSize: SAVED_SEARCH_SCAN_SIZE },
    skip: !isLoggedIn,
    fetchPolicy: "cache-first",
  });

  const wanted = useMemo(() => filterKey(filters), [filters]);

  const alreadySaved = useMemo(() => {
    const items = (data?.savedSearches?.items ?? []) as SavedSearchLike[];

    return items.some(
      (item: SavedSearchLike) =>
        filterKey(
          (item?.filters ?? []).map((filter: SavedSearchFilterLike) => ({
            key: String(filter?.key ?? ""),
            value: String(filter?.value ?? ""),
          }))
        ) === wanted
    );
  }, [data, wanted]);

  const saved = alreadySaved || justSaved;

  // Nothing to watch for. Better no button than one that saves an alert
  // matching the entire catalogue.
  if (!filters.length) {
    return null;
  }

  const handleClick = async () => {
    const pass = await guestGuard({
      title: t("pdp:pdp-sold-alert-wall-title", {
        defaultValue: "Get notified when a similar piece arrives",
      }),
      description: t("pdp:pdp-sold-alert-wall-desc", {
        defaultValue:
          "Create an account to save this alert. We will email you when a comparable item is listed.",
      }),
      referrer: "sold-pdp-alert",
    });

    if (!pass) {
      return;
    }

    try {
      await createSavedSearch({ variables: { filters, title, query: null } });

      /** Trigger `save_alert_sold_pdp` event. */
      analyticsService.trackSaveAlertSoldPdp();

      setJustSaved(true);
    } catch (error) {
      console.error("SoldSimilarItemAlert:createSavedSearch", error);

      addToast(
        t("pdp:pdp-sold-alert-error", {
          defaultValue: "We could not save this alert. Please try again.",
        }),
        { appearance: "error" }
      );
    }
  };

  /*
    `InfoPanel` takes an explicit prop list and does not spread the rest,
    so the two data attributes the page is inspected and tested through
    live on a wrapper rather than on the panel itself. Widening the shared
    component to spread arbitrary props is a change to every surface that
    uses it, and this page is not the place to make it.
  */
  return (
    <div
      data-testid="sold-similar-alert"
      data-alert-saved={String(saved)}
      className={className}
    >
      <InfoPanel
        color="brand"
        corners="rounded"
        className="p-4"
        icon={<NotificationBellIcon className="size-6 shrink-0" />}
        body={
          <div className="flex min-w-0 flex-col gap-1">
            <Heading as="h2" size="h6">
              {t("pdp:pdp-sold-alert-title", {
                defaultValue: "Want one like this?",
              })}
            </Heading>

            <Body as="p" className="text-sm">
              {saved
                ? t("pdp:pdp-sold-alert-saved", {
                    defaultValue:
                      "Your alert is on. We will let you know when a comparable piece is listed.",
                  })
                : t("pdp:pdp-sold-alert-body", {
                    defaultValue:
                      "We will email you when a comparable piece is listed.",
                  })}
            </Body>
            {saved ? (
              <Link
                href={ACCOUNT_SEARCHES_ROUTE}
                data-testid="sold-similar-alert-saved"
                className="font-body text-sm text-brand-500 underline"
              >
                {t("pdp:pdp-sold-alert-manage", {
                  defaultValue: "Manage your alerts",
                })}
              </Link>
            ) : (
              /*
            The bell sits LEFT of the label, per the documented icon rule:
            an icon that reinforces what the action does goes before it, an
            icon that says where the action leads goes after. It was on the
            right here, which read as navigation.

            `NotificationBell` is untyped JS whose destructured `className`
            param types as required under `noImplicitAny`, so it cannot be
            rendered bare from a .tsx file.
          */
              <Button
                data-testid="sold-similar-alert-button"
                size="small"
                color="brand"
                appearance="solid"
                className="mt-2 self-start"
                disabled={loading}
                onClick={handleClick}
                leftIcon={<NotificationBellIcon className="size-5" />}
              >
                {loading
                  ? t("pdp:pdp-sold-alert-saving", { defaultValue: "Saving" })
                  : t("pdp:pdp-sold-alert-cta", { defaultValue: "Notify me" })}
              </Button>
            )}
          </div>
        }
      />
    </div>
  );
};

export default SoldSimilarItemAlert;
